import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface CaseHistoryErrorBoundaryProps {
  children: ReactNode;
}

interface CaseHistoryErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary for case history components.
 * Catches errors without exposing PHI in error messages.
 * HIPAA compliant - only logs generic error info.
 */
export class CaseHistoryErrorBoundary extends Component<
  CaseHistoryErrorBoundaryProps,
  CaseHistoryErrorBoundaryState
> {
  constructor(props: CaseHistoryErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): CaseHistoryErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    // HIPAA: Log generic error only, never log error.message (may contain PHI)
    console.error('CaseHistory component error');
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-sm text-destructive">Failed to load history.</p>
          <p className="text-xs text-muted-foreground mt-1">Please try again later.</p>
        </div>
      );
    }

    return this.props.children;
  }
}
